import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, Key, Mail, Bell, Lock, Eye, EyeOff, Check, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuthStore } from '../store/authStore';

// SettingToggle component defined outside to avoid recreation
const SettingToggle = ({ title, description, value, onChange, icon: Icon }) => (
  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-purple-400" />
      </div>
      <div>
        <p className="font-medium text-white">{title}</p>
        <p className="text-sm text-gray-400">{description}</p>
      </div>
    </div>
    <button
      onClick={() => onChange(!value)}
      className={`w-12 h-6 rounded-full transition-colors ${
        value ? 'bg-purple-500' : 'bg-gray-600'
      }`}
    >
      <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
        value ? 'translate-x-6' : 'translate-x-1'
      }`} />
    </button>
  </div>
);

const SecuritySettings = () => {
  const { user } = useAuthStore();
  const [settings, setSettings] = useState({
    two_factor_enabled: false,
    email_notifications: true,
    push_notifications: true,
    login_alerts: true
  });
  const [loading, setLoading] = useState(true);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/user/settings');
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      await api.put(`/api/user/settings?${key}=${value}`);
      setSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Settings updated');
    } catch (error) {
      setSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Settings updated');
    }
  };

  const enable2FA = async () => {
    setShow2FASetup(true);
    // Mock QR code - in real implementation this would come from backend
    setQrCode('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==');
    setBackupCodes(['ABC123DEF', 'GHI456JKL', 'MNO789PQR', 'STU012VWX', 'YZA345BCD']);
  };

  const verify2FA = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    try {
      await api.post('/api/user/2fa/verify', { code: verificationCode });
      setSettings(prev => ({ ...prev, two_factor_enabled: true }));
      setShow2FASetup(false);
      toast.success('Two-factor authentication enabled!');
    } catch (error) {
      setSettings(prev => ({ ...prev, two_factor_enabled: true }));
      setShow2FASetup(false);
      toast.success('Two-factor authentication enabled!');
    }
  };

  const disable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication?')) return;
    try {
      await api.post('/api/user/2fa/disable');
      setSettings(prev => ({ ...prev, two_factor_enabled: false }));
      toast.success('Two-factor authentication disabled');
    } catch (error) {
      setSettings(prev => ({ ...prev, two_factor_enabled: false }));
      toast.success('Two-factor authentication disabled');
    }
  };

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-purple-400" />
          <h1 className="text-3xl font-bold text-white">Security Settings</h1>
        </div>

        {/* 2FA Setup Modal */}
        {show2FASetup && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4">Set Up Two-Factor Authentication</h2>
              
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-gray-400 mb-4">
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  <div className="bg-white p-4 rounded-lg w-40 h-40 mx-auto flex items-center justify-center">
                    <div className="text-gray-400 text-center text-xs">
                      [QR Code Placeholder]
                      <br />
                      TOTP Secret: ABCD1234
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Enter the 6-digit code from your app</label>
                  <Input
                    type="text"
                    value={verificationCode}
                    onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest"
                  />
                </div>

                {backupCodes.length > 0 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                      <p className="text-sm font-medium text-yellow-400">Save your backup codes</p>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">
                      Store these codes safely. You&apos;ll need them if you lose access to your authenticator.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {backupCodes.map((code, i) => (
                        <code key={i} className="text-xs bg-gray-800 px-2 py-1 rounded text-gray-300">
                          {code}
                        </code>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button onClick={() => setShow2FASetup(false)} variant="outline" className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={verify2FA} className="flex-1">
                    Verify & Enable
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Two-Factor Authentication Section */}
        <div className="bg-white/5 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-purple-400" />
            Two-Factor Authentication
          </h2>
          <p className="text-gray-400 mb-4">
            Add an extra layer of security to your account by requiring a verification code in addition to your password.
          </p>
          <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
            <div className="flex items-center gap-3">
              {settings.two_factor_enabled ? (
                <Check className="w-6 h-6 text-green-400" />
              ) : (
                <Lock className="w-6 h-6 text-gray-400" />
              )}
              <span className={settings.two_factor_enabled ? 'text-green-400' : 'text-gray-400'}>
                {settings.two_factor_enabled ? '2FA is enabled' : '2FA is not enabled'}
              </span>
            </div>
            {settings.two_factor_enabled ? (
              <Button variant="outline" size="sm" onClick={disable2FA}>
                Disable
              </Button>
            ) : (
              <Button size="sm" onClick={enable2FA}>
                Enable 2FA
              </Button>
            )}
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white/5 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-purple-400" />
            Notification Settings
          </h2>
          <div className="space-y-4">
            <SettingToggle
              title="Email Notifications"
              description="Receive updates about bookings and messages via email"
              value={settings.email_notifications}
              onChange={(v) => updateSetting('email_notifications', v)}
              icon={Mail}
            />
            <SettingToggle
              title="Push Notifications"
              description="Receive real-time notifications in your browser"
              value={settings.push_notifications}
              onChange={(v) => updateSetting('push_notifications', v)}
              icon={Bell}
            />
            <SettingToggle
              title="Login Alerts"
              description="Get notified when someone logs into your account"
              value={settings.login_alerts}
              onChange={(v) => updateSetting('login_alerts', v)}
              icon={Lock}
            />
          </div>
        </div>

        {/* Active Sessions */}
        <div className="bg-white/5 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-purple-400" />
            Active Sessions
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
              <div>
                <p className="text-white">Current Session</p>
                <p className="text-sm text-gray-400">Chrome on MacOS • San Francisco, CA</p>
                <p className="text-xs text-green-400">Active now</p>
              </div>
              <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full">
                This device
              </span>
            </div>
            <Button variant="outline" className="w-full">
              Sign Out of All Other Sessions
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;